import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Search, ArrowUpDown } from 'lucide-react';
import { api } from '../../context/AuthContext';

const eur = (v) => (v || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
const fmt = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleDateString('de-DE'); } catch { return iso; } };
const ART = { akonto: 'Akonto', raten: 'Raten' };
const SORT = [['ueberfaellig', 'Überfällig'], ['offen', 'Offen'], ['aelteste', 'Älteste'], ['name', 'Name']];

const Kpi = ({ label, wert, ton, testId }) => (
  <div className="flex-1 min-w-[150px] bg-white border border-gray-200 px-4 py-3" data-testid={testId}>
    <p className="text-[10px] tracking-[0.2em] uppercase text-[#7a8a99]">{label}</p>
    <p className={`font-serif text-2xl ${ton}`}>{wert}</p>
  </div>
);

const sortieren = (items, key) => [...items].sort((a, b) => (key === 'name' ? (a.name || '').localeCompare(b.name || '') : key === 'aelteste' ? (a.aelteste || '').localeCompare(b.aelteste || '') : b[key] - a[key]));

export const OffenePosten = () => {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('ueberfaellig');
  useEffect(() => { api.get('/register/offene-posten').then((r) => setData(r.data)).catch(() => setData({ items: [], summe: {}, error: true })); }, []);
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return sortieren((data?.items || []).filter((i) => !t || (i.name || '').toLowerCase().includes(t) || (i.mandanten_nr || '').includes(t)), sort);
  }, [data, q, sort]);

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-[#b8893d]" /></div>;
  const su = data.summe || {};
  return (
    <div className="space-y-4" data-testid="offene-posten">
      <div className="flex flex-wrap gap-3">
        <Kpi label="Überfällig gesamt" wert={eur(su.ueberfaellig)} ton="text-rose-700" testId="op-summe-ueberfaellig" />
        <Kpi label="Offen gesamt" wert={eur(su.offen)} ton="text-[#b8893d]" testId="op-summe-offen" />
        <Kpi label="Mandanten" wert={su.mandanten || 0} ton="text-[#1B3A5C]" testId="op-summe-mandanten" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 border border-gray-200 bg-white px-2.5 py-1.5 flex-1 min-w-[200px] focus-within:border-[#b8893d]">
          <Search className="w-3.5 h-3.5 text-[#7a8a99]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name oder Mandantennummer" className="flex-1 text-sm focus:outline-none" data-testid="op-search" />
        </div>
        <ArrowUpDown className="w-3.5 h-3.5 text-[#7a8a99]" />
        {SORT.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setSort(k)} data-testid={`op-sort-${k}`}
            className={`px-3 py-1 text-[11px] rounded-full border transition-colors ${sort === k ? 'bg-[#1B3A5C] border-[#1B3A5C] text-white' : 'bg-white border-gray-200 text-[#1B3A5C] hover:border-[#b8893d]'}`}>{l}</button>
        ))}
      </div>
      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table className="w-full text-xs min-w-[640px]">
          <thead><tr className="text-left text-[10px] tracking-[0.15em] uppercase text-[#7a8a99] border-b border-gray-100">
            <th className="px-3 py-2">Mandant</th><th className="px-3 py-2">Posten</th><th className="px-3 py-2">Seit</th><th className="px-3 py-2 text-right">Offen</th><th className="px-3 py-2 text-right">Überfällig</th>
          </tr></thead>
          <tbody>
            {shown.map((z) => (
              <tr key={z.mandanten_nr || 'ohne'} onClick={() => z.mandanten_nr && nav(`/kanzlei/mandanten/${z.mandanten_nr}`)} data-testid={`op-row-${z.mandanten_nr || 'ohne'}`}
                className={`border-b border-gray-50 ${z.mandanten_nr ? 'cursor-pointer hover:bg-[#f5f8fa]' : 'bg-rose-50/40'}`}>
                <td className="px-3 py-2"><span className="block text-[#1B3A5C] font-medium">{z.name || '—'}</span><span className="font-mono text-[10px] text-[#7a8a99]">{z.mandanten_nr ? `M-${z.mandanten_nr}` : 'nicht zugeordnet'}</span></td>
                <td className="px-3 py-2 text-[#34536b]">{z.posten} · {z.arten.map((a) => ART[a] || a).join(', ')}</td>
                <td className="px-3 py-2 text-[#7a8a99]">{fmt(z.aelteste)}</td>
                <td className="px-3 py-2 text-right font-mono text-[#b8893d]">{eur(z.offen)}</td>
                <td className={`px-3 py-2 text-right font-mono ${z.ueberfaellig ? 'text-rose-700 font-semibold' : 'text-[#7a8a99]'}`}>{eur(z.ueberfaellig)}</td>
              </tr>
            ))}
            {shown.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-[#7a8a99]" data-testid="op-leer">{data.error ? 'Laden fehlgeschlagen.' : 'Keine offenen Posten.'}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
